import { Link } from 'react-router-dom'

import { AlertSeverity, DashboardAlert, GetAlertsResponse } from '@/common/api/params/analytics'

import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'

const SEVERITY_STYLE: Record<AlertSeverity, { bg: string; bar: string; label: string }> = {
  critical: {
    bg: 'rgba(220, 38, 38, 0.08)',
    bar: '#DC2626',
    label: '緊急'
  },
  warning: {
    bg: 'rgba(245, 158, 11, 0.10)',
    bar: '#F59E0B',
    label: '注意'
  },
  info: {
    bg: 'rgba(14, 165, 233, 0.08)',
    bar: '#0EA5E9',
    label: '情報'
  }
}

const SEVERITY_ORDER: AlertSeverity[] = ['critical', 'warning', 'info']

interface Props {
  data: GetAlertsResponse
}

/** サイト単位のまとまり。並びは最も重いアラート → 件数の多い順 */
interface SiteAlertGroup {
  siteId: number
  siteName: string
  alerts: DashboardAlert[]
  worst: AlertSeverity
}

// 2026-10-06 追加：同じサイトのアラートが散らばって見づらいため、サイト単位でまとめる
const groupBySite = (alerts: DashboardAlert[]): SiteAlertGroup[] => {
  const map = new Map<number, SiteAlertGroup>()
  for (const a of alerts) {
    const g = map.get(a.site_id)
    if (!g) {
      map.set(a.site_id, {
        siteId: a.site_id,
        siteName: a.site_name,
        alerts: [a],
        worst: a.severity
      })
      continue
    }
    g.alerts.push(a)
    if (SEVERITY_ORDER.indexOf(a.severity) < SEVERITY_ORDER.indexOf(g.worst)) g.worst = a.severity
  }
  const groups = [...map.values()]
  for (const g of groups) {
    g.alerts.sort((x, y) => SEVERITY_ORDER.indexOf(x.severity) - SEVERITY_ORDER.indexOf(y.severity))
  }
  return groups.sort(
    (x, y) =>
      SEVERITY_ORDER.indexOf(x.worst) - SEVERITY_ORDER.indexOf(y.worst) ||
      y.alerts.length - x.alerts.length
  )
}

const SeverityBadge = ({ severity }: { severity: AlertSeverity }) => {
  const style = SEVERITY_STYLE[severity]
  return (
    <span
      className='shrink-0 rounded-sm px-2 py-0.5 text-white'
      style={{ background: style.bar, fontSize: 12, fontWeight: 700 }}
    >
      {style.label}
    </span>
  )
}

const AlertRow = ({ alert }: { alert: DashboardAlert }) => {
  const style = SEVERITY_STYLE[alert.severity]
  return (
    <div className='flex items-center gap-3 rounded-md p-2' style={{ background: style.bg }}>
      <SeverityBadge severity={alert.severity} />
      <div className='flex flex-1 flex-col'>
        <Text weight='bold' size={14}>
          {alert.title}
        </Text>
        <Text size={12} color={TextColor.SECONDARY}>
          {alert.detail}
        </Text>
      </div>
    </div>
  )
}

const SiteAlertCard = ({ group }: { group: SiteAlertGroup }) => {
  const style = SEVERITY_STYLE[group.worst]
  const counts = SEVERITY_ORDER.map((sev) => ({
    sev,
    n: group.alerts.filter((a) => a.severity === sev).length
  })).filter((c) => c.n > 0)
  return (
    <div
      className='flex flex-col gap-2 rounded-md border p-3'
      style={{ borderColor: 'rgba(0, 0, 0, 0.08)', borderLeft: `4px solid ${style.bar}` }}
    >
      <div className='flex items-center gap-3'>
        <Text weight='bold' size={16}>
          {group.siteName}
        </Text>
        <div className='flex flex-1 flex-wrap gap-2'>
          {counts.map(({ sev, n }) => (
            <span key={sev} style={{ fontSize: 12, color: SEVERITY_STYLE[sev].bar, fontWeight: 700 }}>
              {SEVERITY_STYLE[sev].label} {n}件
            </span>
          ))}
        </div>
        <Link
          to={`/sites/${group.siteId}/recommend/dashboard`}
          className='shrink-0 rounded-sm border px-2 py-1'
          style={{ borderColor: style.bar, color: style.bar, fontSize: 12 }}
        >
          サイトを開く
        </Link>
      </div>
      <div className='flex flex-col gap-1.5'>
        {group.alerts.map((a, i) => (
          <AlertRow key={`${a.service}-${i}`} alert={a} />
        ))}
      </div>
    </div>
  )
}

const AlertSection = ({ data }: Props) => {
  if (!data.alerts.length) {
    return (
      <Area>
        <div className='p-2'>
          <Text weight='bold' size={16}>
            要対応アラート
          </Text>
          <Text size={12} color={TextColor.SECONDARY}>
            現在、検出されているアラートはありません。
          </Text>
        </div>
      </Area>
    )
  }

  return (
    <Area>
      <div className='flex flex-col gap-3 p-2'>
        <div className='flex items-center justify-between'>
          <Text weight='bold' size={16}>
            要対応アラート
          </Text>
          <div className='flex gap-3'>
            {SEVERITY_ORDER.map((sev) => (
              <span key={sev} style={{ fontSize: 12 }}>
                <span
                  className='mr-1 inline-block rounded-full px-1.5 py-0.5 text-white'
                  style={{ background: SEVERITY_STYLE[sev].bar, fontSize: 11, fontWeight: 700 }}
                >
                  {SEVERITY_STYLE[sev].label}
                </span>
                {data.counts[sev] ?? 0}件
              </span>
            ))}
          </div>
        </div>
        <div className='flex flex-col gap-3'>
          {groupBySite(data.alerts).map((g) => (
            <SiteAlertCard key={g.siteId} group={g} />
          ))}
        </div>
      </div>
    </Area>
  )
}

export default AlertSection
